import React, { useState } from 'react';
import { walksData as mockWalksData } from '../data/mock';

const DESKTOP_PENTAGON_POSITIONS = {
  1: { x: 50, y: 20 },
  2: { x: 72, y: 36 },
  3: { x: 64, y: 66 },
  4: { x: 36, y: 66 },
  5: { x: 28, y: 36 }
};

const MOBILE_PENTAGON_POSITIONS = {
  1: { x: 50, y: 24 },
  2: { x: 70, y: 40 },
  3: { x: 61, y: 64 },
  4: { x: 39, y: 64 },
  5: { x: 30, y: 40 }
};

const DOT_COLORS = ['bg-[#F5B041]', 'bg-[#E64A2B]', 'bg-[#5B9BD5]', 'bg-[#1A532F]', 'bg-[#A15D2F]'];

const WalksSection = ({ data }) => {
  const walksData = data || mockWalksData;
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [activeSlideByLocation, setActiveSlideByLocation] = useState({});
  const [desktopPopupStyle, setDesktopPopupStyle] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  const hideTimerRef = React.useRef(null);
  const hoverIntentTimerRef = React.useRef(null);
  const dotRefs = React.useRef({});
  const selectedLocation = walksData.locations.find(
    (location) => location.id === selectedLocationId
  );
  const hoveredLocationData = walksData.locations.find(
    (location) => location.id === hoveredLocation
  );
  const desktopLinePoints = walksData.locations
    .map((location) => DESKTOP_PENTAGON_POSITIONS[location.id] || location.position)
    .filter(Boolean);

  const getLocationImages = (location) => {
    if (Array.isArray(location.imageUrls) && location.imageUrls.length > 0) {
      return location.imageUrls;
    }

    if (Array.isArray(location.images) && location.images.length > 0) {
      return location.images
        .map((item) => (typeof item === 'string' ? item : item?.url))
        .filter(Boolean);
    }

    return location.imageUrl ? [location.imageUrl] : [];
  };
  const getActiveSlide = (locationId, totalSlides) => {
    if (totalSlides <= 0) {
      return 0;
    }

    const rawIndex = activeSlideByLocation[locationId] ?? 0;
    return ((rawIndex % totalSlides) + totalSlides) % totalSlides;
  };
  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };
  const clearHoverIntentTimer = () => {
    if (hoverIntentTimerRef.current) {
      clearTimeout(hoverIntentTimerRef.current);
      hoverIntentTimerRef.current = null;
    }
  };
  const scheduleHidePopup = () => {
    clearHideTimer();
    clearHoverIntentTimer();
    hideTimerRef.current = setTimeout(() => {
      setHoveredLocation(null);
    }, 220);
  };
  const showDesktopPopupFor = (locationId) => {
    clearHideTimer();
    clearHoverIntentTimer();

    const delayMs = hoveredLocation ? 160 : 80;
    hoverIntentTimerRef.current = setTimeout(() => {
      setHoveredLocation(locationId);
    }, delayMs);
  };

  React.useEffect(() => {
    const activeLocationId = hoveredLocation ?? selectedLocationId;
    if (!activeLocationId) {
      return undefined;
    }

    const activeLocation = walksData.locations.find(
      (location) => location.id === activeLocationId
    );
    if (!activeLocation) {
      return undefined;
    }

    const totalSlides = getLocationImages(activeLocation).length;
    if (totalSlides <= 1) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setActiveSlideByLocation((prev) => ({
        ...prev,
        [activeLocationId]: ((prev[activeLocationId] ?? 0) + 1) % totalSlides
      }));
    }, 3200);

    return () => clearInterval(intervalId);
  }, [hoveredLocation, selectedLocationId, walksData.locations]);

  React.useEffect(() => {
    if (!hoveredLocationData) {
      return undefined;
    }

    const popupWidth = 460;
    const popupHeight = 320;
    const viewportMargin = 16;
    const verticalOffset = 14;

    const updatePopupPosition = () => {
      const dotElement = dotRefs.current[hoveredLocationData.id];
      if (!dotElement) {
        return;
      }

      const dotRect = dotElement.getBoundingClientRect();
      let left = dotRect.left + dotRect.width / 2 - popupWidth / 2;
      left = Math.max(
        viewportMargin,
        Math.min(left, window.innerWidth - popupWidth - viewportMargin)
      );

      const preferredTop = dotRect.top - popupHeight - verticalOffset;
      const hasSpaceAbove = preferredTop >= viewportMargin;
      let top = hasSpaceAbove ? preferredTop : dotRect.bottom + verticalOffset;
      const maxTop = window.innerHeight - popupHeight - viewportMargin;
      top = Math.max(viewportMargin, Math.min(top, maxTop));

      setDesktopPopupStyle({
        left: `${left}px`,
        top: `${top}px`,
        width: `${popupWidth}px`
      });
    };

    updatePopupPosition();
    window.addEventListener('resize', updatePopupPosition);
    window.addEventListener('scroll', updatePopupPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition, true);
    };
  }, [hoveredLocationData]);

  React.useEffect(
    () => () => {
      clearHideTimer();
      clearHoverIntentTimer();
    },
    []
  );
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateViewportMode = () => setIsMobileViewport(mediaQuery.matches);

    updateViewportMode();
    mediaQuery.addEventListener('change', updateViewportMode);

    return () => mediaQuery.removeEventListener('change', updateViewportMode);
  }, []);

  return (
    <section
      id="walks"
      className="min-h-screen bg-gradient-to-br from-[#F5F5F0] to-[#1A532F]/10 py-20 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div 
          className="inline-block bg-[#1A532F] text-white px-6 py-3 rounded-full mb-12 font-bold text-sm"
          style={{ fontFamily: "'Press Start 2P', monospace" }}
        >
          {walksData.title}
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Ideology Text */}
          <div>
            <p className="text-[#1A532F] text-xl md:text-2xl leading-relaxed font-light">
              {walksData.ideology}
            </p>
          </div>

          {/* Right: Interactive Map */}
          <div className="relative h-[500px]">
            {/* Connector lines for walk points */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden md:block" viewBox="0 0 100 100" preserveAspectRatio="none">
              {desktopLinePoints.map((point, index) => {
                const nextPoint = desktopLinePoints[(index + 1) % desktopLinePoints.length];
                if (!nextPoint || desktopLinePoints.length < 3) {
                  return null;
                }

                return (
                  <line
                    key={`walk-connector-${index}`}
                    x1={point.x}
                    y1={point.y}
                    x2={nextPoint.x}
                    y2={nextPoint.y}
                    stroke="#1A532F"
                    strokeWidth="0.3"
                    opacity="0.3"
                  />
                );
              })}
            </svg>

            {/* Interactive Dots in Pentagon Formation */}
            {walksData.locations.map((location, index) => {
              const isActiveHovered = hoveredLocation === location.id;
              const displayPosition =
                isMobileViewport && MOBILE_PENTAGON_POSITIONS[location.id]
                  ? MOBILE_PENTAGON_POSITIONS[location.id]
                  : DESKTOP_PENTAGON_POSITIONS[location.id] || location.position;

              return (
              <div
                key={location.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10 transition-opacity duration-200"
                style={{
                  left: `${displayPosition.x}%`,
                  top: `${displayPosition.y}%`
                }}
                ref={(element) => {
                  dotRefs.current[location.id] = element;
                }}
                onMouseEnter={() => showDesktopPopupFor(location.id)}
                onMouseLeave={scheduleHidePopup}
                onClick={() =>
                  setSelectedLocationId((prev) =>
                    prev === location.id ? null : location.id
                  )
                }
              >
                {/* Solid color dot - no icon */}
                <div
                  className={`relative transition-all duration-300 ${
                    isActiveHovered
                      ? 'scale-150'
                      : 'scale-100 hover:scale-125'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full ${
                      DOT_COLORS[index % DOT_COLORS.length]
                    } shadow-lg`}
                  >
                  </div>
                </div>

                {/* Label below dot */}
                <div
                  className="absolute top-full left-1/2 transform whitespace-nowrap mt-3 -translate-x-1/2"
                >
                  <span className="text-[#1A532F] font-bold text-sm">
                    {location.name}
                  </span>
                </div>
              </div>
              );
            })}

            {/* Desktop popup rendered once and clamped to viewport */}
            {hoveredLocationData && desktopPopupStyle && (
              <div
                className="hidden md:block fixed z-[9999] animate-in fade-in duration-200"
                style={desktopPopupStyle}
                onMouseEnter={clearHideTimer}
                onMouseLeave={scheduleHidePopup}
              >
                {(() => {
                  const locationImages = getLocationImages(hoveredLocationData);
                  const activeSlide = getActiveSlide(
                    hoveredLocationData.id,
                    locationImages.length
                  );

                  return (
                    <div className="bg-white rounded-xl shadow-2xl overflow-hidden border-4 border-[#1A532F]">
                      <div className="relative h-56 bg-[#F5F5F0]">
                        {locationImages.length > 0 && (
                          <img
                            src={locationImages[activeSlide]}
                            alt={`${hoveredLocationData.name} slide ${activeSlide + 1}`}
                            className={`w-full h-full ${
                              hoveredLocationData.imageFit === 'contain'
                                ? 'object-contain'
                                : 'object-cover'
                            }`}
                            loading="lazy"
                          />
                        )}

                        {locationImages.length > 1 && (
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {locationImages.map((_, dotIndex) => (
                              <button
                                key={`${hoveredLocationData.id}-desktop-dot-${dotIndex}`}
                                type="button"
                                className={`h-2.5 w-2.5 rounded-full ${
                                  activeSlide === dotIndex ? 'bg-white' : 'bg-white/55'
                                }`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setActiveSlideByLocation((prev) => ({
                                    ...prev,
                                    [hoveredLocationData.id]: dotIndex
                                  }));
                                }}
                                aria-label={`View image ${dotIndex + 1} for ${hoveredLocationData.name}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#1A532F] text-lg mb-2">
                          {hoveredLocationData.title || hoveredLocationData.name}
                        </h3>
                        {hoveredLocationData.subtitle && (
                          <p className="text-[#1A532F]/80 text-sm italic mb-2">
                            {hoveredLocationData.subtitle}
                          </p>
                        )}
                        <p className="text-[#1A532F]/70 text-sm whitespace-pre-line">
                          {hoveredLocationData.description}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile centered popup - not attached to dot */}
      {selectedLocation && (
        <div
          className="fixed inset-0 z-[10000] bg-black/45 flex items-center justify-center p-4 md:hidden"
          onClick={() => setSelectedLocationId(null)}
        >
          {(() => {
            const selectedImages = getLocationImages(selectedLocation);
            const selectedActiveSlide = getActiveSlide(selectedLocation.id, selectedImages.length);

            return (
          <div
            className="w-full bg-white rounded-xl shadow-2xl overflow-hidden border-4 border-[#1A532F] animate-in fade-in duration-200"
            style={{ maxWidth: '560px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b-2 border-[#1A532F]/15">
              <h3 className="font-bold text-[#1A532F] text-base">
                {selectedLocation.title || selectedLocation.name}
              </h3>
              <button
                onClick={() => setSelectedLocationId(null)}
                className="text-[#1A532F] hover:text-[#E64A2B] font-bold px-2"
                aria-label="Close walk details"
              >
                X
              </button>
            </div>

            <div className="relative h-64 bg-[#F5F5F0]">
              {selectedImages.length > 0 && (
                <img
                  src={selectedImages[selectedActiveSlide]}
                  alt={`${selectedLocation.name} slide ${selectedActiveSlide + 1}`}
                  className={`w-full h-full ${
                    selectedLocation.imageFit === 'contain'
                      ? 'object-contain'
                      : 'object-cover'
                  }`}
                  loading="lazy"
                />
              )}

              {selectedImages.length > 1 && (
                <>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {selectedImages.map((_, dotIndex) => (
                      <button
                        key={`${selectedLocation.id}-mobile-dot-${dotIndex}`}
                        type="button"
                        className={`h-2.5 w-2.5 rounded-full ${
                          selectedActiveSlide === dotIndex ? 'bg-white' : 'bg-white/55'
                        }`}
                        onClick={() =>
                          setActiveSlideByLocation((prev) => ({
                            ...prev,
                            [selectedLocation.id]: dotIndex
                          }))
                        }
                        aria-label={`View image ${dotIndex + 1} for ${selectedLocation.name}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="p-4">
              {selectedLocation.subtitle && (
                <p className="text-[#1A532F]/80 text-sm italic mb-2">
                  {selectedLocation.subtitle}
                </p>
              )}
              <p className="text-[#1A532F]/70 text-sm whitespace-pre-line">
                {selectedLocation.description}
              </p>
            </div>
          </div>
            );
          })()}
        </div>
      )}
    </section>
  );
};

export default WalksSection;
